import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { supabase } from '../api/supabase';
import { User } from '../types';
import { 
  ROLE_PERMISSIONS, 
  PERMISSION_DEPENDENCIES, 
  Permission, 
  Role 
} from '../constants/permissions';
import { comparePassword } from '../utils/passwordHash';

// Check if authentication is enabled
const isAuthEnabled = process.env.REACT_APP_AUTH_ENABLED !== 'false';

// Default user for when auth is disabled
const defaultUser: User = {
  id: 'dev-user',
  email: 'dev@example.com',
  role: 'admin',
  created_at: new Date().toISOString(),
};

// Web-compatible AsyncStorage implementation with protection against overwriting valid sessions
const AsyncStorage = {
  getItem: async (key: string) => {
    try {
      const value = localStorage.getItem(key);
      console.log(`📦 localStorage GET ${key}:`, value ? JSON.parse(value).state?.user?.name || 'null' : 'null');
      return value;
    } catch {
      return null;
    }
  },
  setItem: async (key: string, value: string) => {
    try {
      const parsed = JSON.parse(value);
      const newUserName = parsed.state?.user?.name || null;
      
      console.log(`💾 localStorage SET ${key}:`, newUserName || 'null (logout or initial state)');
      localStorage.setItem(key, value);
    } catch {
      // Ignore errors in web environment
    }
  },
  removeItem: async (key: string) => {
    try {
      console.log(`🗑️ localStorage REMOVE ${key}`);
      localStorage.removeItem(key);
    } catch {
      // Ignore errors in web environment
    }
  },
};

// Extended User type with permissions
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  permissions_added: Permission[];
  permissions_removed: Permission[];
  is_active: boolean;
  user_id?: string | null; // For super admins using Supabase Auth
  created_at: string;
  last_login?: string | null;
}

interface AuthState {
  user: AdminUser | null;
  session: any | null;
  loading: boolean;
  error: string | null;
  isAuthEnabled: boolean;
  isValidatingSession: boolean; // NEW: Track session validation state
  
  // Core auth methods
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  
  // Permission helpers
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  hasRole: (role: Role) => boolean;
  getUserPermissions: () => Permission[];
  
  // Session management
  checkSession: () => Promise<boolean>;
  
  // Legacy compatibility
  setUser: (user: User | null) => void;
  setSession: (session: any | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

/**
 * Calculate user's final permissions based on role and custom permissions
 */
function calculateUserPermissions(user: AdminUser): Permission[] {
  // Get role's default permissions
  const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
  
  // Add custom permissions
  const addedPermissions = user.permissions_added || [];
  
  // Remove blocked permissions
  const removedPermissions = user.permissions_removed || [];
  
  // Combine: (role permissions + added) - removed
  const allPermissions = [...rolePermissions, ...addedPermissions];
  const finalPermissions = allPermissions.filter(
    (perm) => !removedPermissions.includes(perm)
  );
  
  // Remove duplicates
  return [...new Set(finalPermissions)];
}

/**
 * Get all required permissions including dependencies
 */
function getPermissionsWithDependencies(permissions: Permission[]): Permission[] {
  const result = new Set<Permission>(permissions);
  
  permissions.forEach((permission) => {
    const deps = PERMISSION_DEPENDENCIES[permission];
    if (deps) {
      deps.forEach((dep) => result.add(dep));
    }
  });
  
  return Array.from(result);
}

/**
 * Get initial user state from localStorage to prevent session loss on hot reload
 * ENHANCED: Strict validation to prevent "User" fallback
 */
function getInitialUserFromStorage(): AdminUser | null {
  if (!isAuthEnabled) return defaultUser as any;
  
  try {
    const stored = localStorage.getItem('auth-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      const user = parsed?.state?.user;
      
      // CRITICAL: Validate user has ALL required fields
      const hasValidData = user && 
                          user.id && 
                          user.name && 
                          user.name.trim() !== '' &&
                          user.email && 
                          user.email.trim() !== '' &&
                          user.role;
      
      if (hasValidData) {
        console.log('🔄 Restoring valid user from localStorage:', user.name);
        return user;
      } else {
        console.warn('⚠️ Found incomplete user in localStorage, forcing logout');
        console.log('User data:', { 
          hasId: !!user?.id, 
          hasName: !!(user?.name && user.name.trim() !== ''), 
          hasEmail: !!(user?.email && user.email.trim() !== ''),
          hasRole: !!user?.role 
        });
        
        // FORCE CLEAR: Remove broken session immediately
        localStorage.removeItem('auth-storage');
        return null;
      }
    }
  } catch (e) {
    console.warn('Failed to restore user from localStorage:', e);
    // Clear corrupted storage
    localStorage.removeItem('auth-storage');
  }
  
  return null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: getInitialUserFromStorage(),
      session: isAuthEnabled ? null : { user: defaultUser },
      loading: false,
      error: null,
      isAuthEnabled,
      isValidatingSession: false,
      
      /**
       * Login with email and password
       * Supports both Supabase Auth (super admins) and custom auth (regular users)
       */
      login: async (email, password) => {
        if (!isAuthEnabled) {
          set({ 
            user: defaultUser as any,
            session: { user: defaultUser },
            loading: false,
            error: null
          });
          return;
        }

        set({ loading: true, error: null });
        
        try {
          // Step 1: Fetch user from admin_users table by email
          const { data: adminUser, error: fetchError } = await supabase
            .from('admin_users')
            .select('*')
            .eq('email', email)
            .eq('is_active', true)
            .single();
          
          if (fetchError || !adminUser) {
            throw new Error('Invalid email or password');
          }
          
          // Step 2: Check authentication method
          if (adminUser.user_id) {
            // Super Admin - use Supabase Auth
            const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
              email,
              password,
            });
            
            if (authError) {
              console.error('Supabase Auth failed:', authError.message);
              throw new Error('Invalid email or password');
            }
            
            // Update last_login
            await supabase
              .from('admin_users')
              .update({ last_login: new Date().toISOString() })
              .eq('id', adminUser.id);
            
            // VALIDATION: Ensure user has name field
            if (!adminUser.name || adminUser.name.trim() === '') {
              throw new Error('User account is missing required information (name). Please contact administrator.');
            }
            
            const userData = {
              id: adminUser.id,
              email: adminUser.email,
              name: adminUser.name,
              role: adminUser.role,
              permissions_added: adminUser.permissions_added || [],
              permissions_removed: adminUser.permissions_removed || [],
              is_active: adminUser.is_active,
              user_id: adminUser.user_id,
              created_at: adminUser.created_at,
              last_login: new Date().toISOString(),
            };
            
            console.log('✅ Super Admin login successful:', userData.name);
            
            set({
              user: userData,
              session: authData.session,
              loading: false,
            });
          } else if (adminUser.password_hash) {
            // Regular User - use custom auth with expo-crypto
            const isValidPassword = await comparePassword(password, adminUser.password_hash);
            
            if (!isValidPassword) {
              throw new Error('Invalid email or password');
            }
            
            // VALIDATION: Ensure user has name field
            if (!adminUser.name || adminUser.name.trim() === '') {
              throw new Error('User account is missing required information (name). Please contact administrator.');
            }
            
            // Update last_login
            await supabase
              .from('admin_users')
              .update({ last_login: new Date().toISOString() })
              .eq('id', adminUser.id);
            
            const userData = {
              id: adminUser.id,
              email: adminUser.email,
              name: adminUser.name,
              role: adminUser.role,
              permissions_added: adminUser.permissions_added || [],
              permissions_removed: adminUser.permissions_removed || [],
              is_active: adminUser.is_active,
              created_at: adminUser.created_at,
              last_login: new Date().toISOString(),
            };
            
            console.log('✅ Regular user login successful:', userData.name);
            
            set({
              user: userData,
              session: { user: adminUser }, // Custom session
              loading: false,
            });
          } else {
            throw new Error('User account is not properly configured');
          }
        } catch (error: any) {
          console.error('Login error:', error);
          const errorMessage = error?.message || 'Failed to sign in. Please try again.';
          set({ loading: false, error: errorMessage });
          throw new Error(errorMessage);
        }
      },
      
      /**
       * Check if session is still valid and refresh user data if needed
       * OPTIMIZED: Silent background check without blocking UI
       */
      checkSession: async () => {
        const { user } = get();
        if (!user || !user.id) {
          return false;
        }
        
        // CRITICAL FIX: Don't block UI with isValidatingSession
        // This check runs in background every 2 minutes
        
        try {
          // Verify user still exists and is active in database
          const { data, error } = await supabase
            .from('admin_users')
            .select('*')
            .eq('id', user.id)
            .eq('is_active', true)
            .single();
          
          if (error) {
            // Don't logout on network/API errors (406, 500, etc.)
            // Only logout if user explicitly doesn't exist (404) or is marked inactive
            if (error.code === 'PGRST116') { // PostgreSQL "not found" error
              console.warn('⚠️ User not found in database, logging out');
              await get().logout();
              return false;
            }
            
            // For other errors (network, 406, etc.), keep session and retry later
            // Silently fail - don't spam console
            return false;
          }
          
          if (!data) {
            console.warn('⚠️ Session invalid: user data is null, logging out');
            await get().logout();
            return false;
          }
          
          // Update user data if it changed (e.g., permissions updated)
          const currentUserStr = JSON.stringify(user);
          const newUserStr = JSON.stringify(data);
          if (currentUserStr !== newUserStr) {
            console.log('✅ User data changed, updating session');
            set({ user: data });
          }
          
          return true;
        } catch (error) {
          // Silently fail - don't logout on exceptions
          return false;
        }
      },
      
      /**
       * Logout current user
       * ENHANCED: Aggressive cleanup and forced state reset
       */
      logout: async () => {
        if (!isAuthEnabled) {
          return;
        }

        console.log('🚪 Logout initiated');
        set({ loading: true, error: null });
        
        try {
          const { user } = get();
          
          // If user was using Supabase Auth, sign out from Supabase
          if (user?.user_id) {
            console.log('Logging out from Supabase Auth');
            await supabase.auth.signOut();
          }
          
          // CRITICAL: Clear all state FIRST
          set({ 
            user: null, 
            session: null, 
            loading: false,
            error: null,
            isValidatingSession: false,
          });
          
          // CRITICAL: Aggressively clear all storage
          console.log('🗑️ Clearing all auth storage');
          localStorage.removeItem('auth-storage');
          
          // Double-check: Force clear even if key name changed
          Object.keys(localStorage).forEach(key => {
            if (key.includes('auth') || key.includes('user')) {
              console.log(`�️ Removing suspicious key: ${key}`);
              localStorage.removeItem(key);
            }
          });
          
          console.log('✅ Logout complete - all storage cleared');
          
          // On web, force a page reload to ensure complete state reset
          if (typeof window !== 'undefined' && window.location) {
            console.log('🔄 Forcing page reload to reset state');
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }
        } catch (error: any) {
          console.error('Logout error:', error);
          // Even if logout fails, clear local state
          set({ user: null, session: null, loading: false, error: null });
          localStorage.removeItem('auth-storage');
          
          // Still reload on error
          if (typeof window !== 'undefined' && window.location) {
            setTimeout(() => window.location.reload(), 100);
          }
        }
      },
      
      /**
       * Check if user has a specific permission
       */
      hasPermission: (permission: Permission) => {
        const { user } = get();
        if (!user) return false;
        
        const userPermissions = calculateUserPermissions(user);
        return userPermissions.includes(permission);
      },
      
      /**
       * Check if user has ANY of the given permissions
       */
      hasAnyPermission: (permissions: Permission[]) => {
        const { user } = get();
        if (!user) return false;
        
        const userPermissions = calculateUserPermissions(user);
        return permissions.some((perm) => userPermissions.includes(perm));
      },
      
      /**
       * Check if user has ALL of the given permissions
       */
      hasAllPermissions: (permissions: Permission[]) => {
        const { user } = get();
        if (!user) return false;
        
        const userPermissions = calculateUserPermissions(user);
        return permissions.every((perm) => userPermissions.includes(perm));
      },
      
      /**
       * Check if user has a specific role
       */
      hasRole: (role: Role) => {
        const { user } = get();
        return user?.role === role;
      },
      
      /**
       * Get all permissions for current user (including dependencies)
       */
      getUserPermissions: () => {
        const { user } = get();
        if (!user) return [];
        
        const basePermissions = calculateUserPermissions(user);
        return getPermissionsWithDependencies(basePermissions);
      },
      
      // ===================================================================
      // LEGACY METHODS (for backward compatibility)
      // ===================================================================
      
      setUser: (user) => set({ user: user as any }),
      setSession: (session) => set({ session }),

      signIn: async (email, password) => {
        await get().login(email, password);
      },
      
      signUp: async (email, password) => {
        // Signup is now only through user management screen
        // This method is kept for compatibility but does nothing
        console.warn('signUp is deprecated. Use User Management screen to create users.');
        if (!isAuthEnabled) return;
        
        set({ error: 'User registration is disabled. Please contact an administrator.', loading: false });
        throw new Error('User registration is disabled');
      },
      
      signOut: async () => {
        await get().logout();
      },
      
      resetPassword: async (email) => {
        if (!isAuthEnabled) return;

        set({ loading: true, error: null });
        
        try {
          // Check if user uses Supabase Auth
          const { data: adminUser } = await supabase
            .from('admin_users')
            .select('user_id')
            .eq('email', email)
            .single();
          
          if (adminUser?.user_id) {
            // Super admin - use Supabase password reset
            const { error } = await supabase.auth.resetPasswordForEmail(email);
            if (error) throw error;
            set({ loading: false });
          } else {
            // Regular user - password reset must be done by admin
            set({ 
              loading: false, 
              error: 'Password reset must be done by an administrator. Please contact your supervisor.' 
            });
            throw new Error('Password reset not available for regular users');
          }
        } catch (error: any) {
          set({ loading: false, error: error.message });
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => AsyncStorage),
      // Only persist user and session, NOT loading or error states
      partialize: (state) => ({
        user: state.user,
        session: state.session,
        isAuthEnabled: state.isAuthEnabled,
      }),
      // Validate session on rehydration (when app loads)
      onRehydrateStorage: () => (state) => {
        if (state?.user) {
          // CRITICAL FIX: Validate user has complete data structure
          const hasValidUserData = state.user.id && state.user.name && state.user.email && state.user.role;
          
          if (!hasValidUserData) {
            console.warn('⚠️ Incomplete user data found in localStorage, forcing logout');
            console.log('User data:', { 
              id: state.user.id, 
              name: state.user.name, 
              email: state.user.email, 
              role: state.user.role 
            });
            
            // Force logout - clear everything
            state.user = null;
            state.session = null;
            localStorage.removeItem('auth-storage');
            console.log('🚪 Forced logout due to incomplete user data');
            return;
          }
          
          console.log('💡 Session rehydrated successfully:', state.user.name);
        }
      },
    }
  )
);
